import fixWebmDuration from 'fix-webm-duration';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';

const DB_NAME = 'pencilapp';
const RECORDINGS_STORE_NAME = 'recordings';

// This is the time slice for historical recordings when we were storing
// separate chunks for each 5 seconds without merging them.
// DO NOT CHANGE THIS VALUE WITHOUT TALKING TO AMOGH OR ELWAKEEL FIRST.
const HISTORICAL_RECORDING_TIME_SLICE_MS = 5000;

export interface RecordingMetadata {
  duration?: number;
  deleteOnNextOpen?: boolean;
  title: string;
  timestamp: number;
}

export interface RecordingEntry {
  metadata: RecordingMetadata;
  chunk: Blob | null;
  // Legacy format - for backward compatibility
  chunks?: Blob[];
}

let db: IDBDatabase | null = null;

function getDb(): Promise<IDBDatabase> {
  if (db) {
    return Promise.resolve(db);
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME);

    request.onupgradeneeded = (event) => {
      const dbInstance = (event.target as IDBOpenDBRequest).result;
      if (!dbInstance.objectStoreNames.contains(RECORDINGS_STORE_NAME)) {
        dbInstance.createObjectStore(RECORDINGS_STORE_NAME);
      }
    };

    request.onsuccess = (event) => {
      const dbInstance = (event.target as IDBOpenDBRequest).result;

      dbInstance.onclose = () => {
        db = null;
      };

      if (!dbInstance.objectStoreNames.contains(RECORDINGS_STORE_NAME)) {
        const version = dbInstance.version;
        dbInstance.close();

        const upgradeRequest = indexedDB.open(DB_NAME, version + 1);

        upgradeRequest.onupgradeneeded = (upgradeEvent) => {
          const upgradeDb = (upgradeEvent.target as IDBOpenDBRequest).result;
          if (!upgradeDb.objectStoreNames.contains(RECORDINGS_STORE_NAME)) {
            upgradeDb.createObjectStore(RECORDINGS_STORE_NAME);
          }
        };

        upgradeRequest.onsuccess = (upgradeEvent) => {
          db = (upgradeEvent.target as IDBOpenDBRequest).result;
          db.onclose = () => {
            db = null;
          };
          resolve(db);
        };

        upgradeRequest.onerror = (upgradeEvent) =>
          reject((upgradeEvent.target as IDBOpenDBRequest).error);
      } else {
        db = dbInstance;
        resolve(db);
      }
    };

    request.onerror = (event) => reject((event.target as IDBOpenDBRequest).error);
  });
}

export async function createRecordingEntry(
  recordingId: string,
  title: string,
  timestamp: number,
): Promise<void> {
  const dbInstance = await getDb();
  const transaction = dbInstance.transaction([RECORDINGS_STORE_NAME], 'readwrite');
  const objectStore = transaction.objectStore(RECORDINGS_STORE_NAME);
  const newEntry: RecordingEntry = {
    metadata: { title, timestamp },
    chunk: null,
  };
  objectStore.put(newEntry, recordingId);
}

/**
 * More efficient version that doesn't require a get operation
 * Requires the complete metadata to be provided
 */
export async function storeCompleteEntry(
  recordingId: string,
  blob: Blob,
  completeMetadata: RecordingMetadata,
): Promise<void> {
  const dbInstance = await getDb();
  const transaction = dbInstance.transaction([RECORDINGS_STORE_NAME], 'readwrite');
  const objectStore = transaction.objectStore(RECORDINGS_STORE_NAME);

  const completeEntry: RecordingEntry = {
    metadata: completeMetadata,
    chunk: blob,
  };

  return new Promise((resolve, reject) => {
    const request = objectStore.put(completeEntry, recordingId);
    request.onsuccess = () => resolve();
    request.onerror = (event) => reject(event);
  });
}

export async function getRecordingIds(userId: string): Promise<string[]> {
  const dbInstance = await getDb();
  return new Promise((resolve, reject) => {
    const transaction = dbInstance.transaction([RECORDINGS_STORE_NAME], 'readonly');
    const objectStore = transaction.objectStore(RECORDINGS_STORE_NAME);
    const keysRequest = objectStore.getAllKeys();

    keysRequest.onsuccess = () => {
      const userKeys = (keysRequest.result as IDBValidKey[]).filter(
        (key): key is string => typeof key === 'string' && key.startsWith(`${userId}-`),
      );
      resolve(userKeys);
    };
    keysRequest.onerror = (event) => reject(event);
  });
}

export async function getRecordingEntry(recordingId: string): Promise<RecordingEntry | undefined> {
  const dbInstance = await getDb();
  return new Promise((resolve, reject) => {
    const transaction = dbInstance.transaction([RECORDINGS_STORE_NAME], 'readonly');
    const objectStore = transaction.objectStore(RECORDINGS_STORE_NAME);
    const request = objectStore.get(recordingId);
    request.onsuccess = () => resolve(request.result as RecordingEntry);
    request.onerror = (event) => reject(event);
  });
}

export async function deleteRecording(recordingId: string): Promise<void> {
  const dbInstance = await getDb();
  const transaction = dbInstance.transaction([RECORDINGS_STORE_NAME], 'readwrite');
  const objectStore = transaction.objectStore(RECORDINGS_STORE_NAME);
  objectStore.delete(recordingId);
}

export async function markRecordingForDeletionOnNextOpen(recordingId: string): Promise<void> {
  const dbInstance = await getDb();
  const transaction = dbInstance.transaction([RECORDINGS_STORE_NAME], 'readwrite');
  const objectStore = transaction.objectStore(RECORDINGS_STORE_NAME);

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = (event) => reject(event);
    transaction.onabort = (event) => reject(event);

    const request = objectStore.get(recordingId);
    request.onsuccess = () => {
      const entry = request.result as RecordingEntry | undefined;
      if (!entry) {
        return;
      }

      objectStore.put(
        {
          ...entry,
          metadata: {
            ...entry.metadata,
            deleteOnNextOpen: true,
          },
        },
        recordingId,
      );
    };
  });
}

export async function deleteRecordingsMarkedForDeletionOnNextOpen(): Promise<void> {
  const dbInstance = await getDb();
  const transaction = dbInstance.transaction([RECORDINGS_STORE_NAME], 'readwrite');
  const objectStore = transaction.objectStore(RECORDINGS_STORE_NAME);

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = (event) => reject(event);
    transaction.onabort = (event) => reject(event);

    const request = objectStore.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        return;
      }

      const entry = cursor.value as RecordingEntry;
      const markedForDeletion = entry.metadata?.deleteOnNextOpen;
      const hasNoChunkData = !getRecordingBlob(entry);
      if (markedForDeletion || hasNoChunkData) {
        cursor.delete();
      }

      cursor.continue();
    };
  });
}

export function currentSupportedMimeType(): string {
  if (!MediaRecorder.isTypeSupported('video/webm')) {
    return 'video/mp4';
  }
  return 'video/webm';
}

export function mergeBlobs(existingBlob: Blob | null, newBlob: Blob): Blob {
  if (!existingBlob) {
    return newBlob;
  }
  return new Blob([existingBlob, newBlob], { type: newBlob.type });
}

/**
 * Helper function to get the recording blob from either new or legacy format
 */
export function getRecordingBlob(entry: RecordingEntry): Blob | null {
  // New format - single chunk
  if (entry.chunk) {
    return entry.chunk;
  }

  // Legacy format - multiple chunks
  if (entry.chunks && entry.chunks.length > 0) {
    return new Blob(entry.chunks, { type: entry.chunks[0]?.type });
  }

  return null;
}

/**
 * Helper function to get the recording duration, falling back to chunk-based calculation for legacy recordings
 */
export function getRecordingDuration(entry: RecordingEntry): number | undefined {
  // If duration is stored in metadata, use it
  if (entry.metadata.duration) {
    return entry.metadata.duration;
  }

  // Legacy fallback - estimate duration based on number of chunks
  if (entry.chunks && entry.chunks.length > 0) {
    return entry.chunks.length * HISTORICAL_RECORDING_TIME_SLICE_MS;
  }

  return undefined;
}

export async function fixAndDownload(blob: Blob, duration: number | undefined, filename: string) {
  let blobToDownload = blob;
  if (currentSupportedMimeType() === 'video/webm' && duration) {
    try {
      blobToDownload = await fixWebmDuration(blob, duration, { logger: false });
    } catch (error) {
      console.warn('Failed to fix WEBM duration:', error);
    }
  }
  const url = URL.createObjectURL(blobToDownload);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.${currentSupportedMimeType().split('/')[1]}`;
  a.click();
  modifiedSetTimeout(() => {
    // give the browser some time to download the file before revoking the object URL
    URL.revokeObjectURL(url);
  }, 30000);
}
